import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API, PRIVATE_API } from "../../api/apiIndex";

const _accessToken = JSON.parse(localStorage.getItem("accessToken"));

const initialState = {
  accessToken: _accessToken ? _accessToken : null,
  data: null,
  error: "",
  status: "idle",
  registerError: "",
  registerStatus: "idle",
  forgotPasswordStatus: "idle",
  forgotPasswordError: "",
  resetPasswordStatus: "idle",
  resetPasswordError: "",
  verifyUserStatus: "idle",
  verifyUserError: "",
  googleLoginStatus: "idle",
  googleLoginError: "",
  loginWithTokenStatus: "idle",
  loginWithTokenError: "",
};

export const login = createAsyncThunk(
  "user/login",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await API.post("/user/login", userData);
      localStorage.setItem(
        "accessToken",
        JSON.stringify(response.data.accessToken)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const signup = createAsyncThunk(
  "user/signup",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await API.post("/user/signup", userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await API.post("/user/password/forgot", userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async (userData, { rejectWithValue }) => {
    try {
      const { token, data } = userData;
      const response = await API.put(`/user/password/reset/${token}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const loginwithtoken = createAsyncThunk(
  "user/loginwithtoken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PRIVATE_API.get("/user/getByToken");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const loginWithGoogle = createAsyncThunk(
  "user/loginWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get("/auth/google/success", {
        withCredentials: true,
      });
      localStorage.setItem(
        "accessToken",
        JSON.stringify(response.data.user.accessToken)
      );
      return response.data.user;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const verifyUser = createAsyncThunk(
  "user/verifyUser",
  async (token, { rejectWithValue }) => {
    try {
      const response = await API.put(`/user/verify/${token}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

export const logout = createAsyncThunk(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get("auth/logout", {
        withCredentials: true,
      });
      localStorage.removeItem("accessToken");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    removeLoginMessage: (state) => {
      state.error = "";
      state.status = "idle";
    },
    removeRegisterMessage: (state) => {
      state.registerError = "";
      state.registerStatus = "idle";
    },
    removeForgotPasswordMessage: (state) => {
      state.forgotPasswordError = "";
      state.forgotPasswordStatus = "idle";
    },
    removeResetPasswordMessage: (state) => {
      state.resetPasswordStatus = "idle";
      state.resetPasswordError = "";
    },
    removeVerifyUserMessage: (state) => {
      state.verifyUserStatus = "idle";
      state.verifyUserError = "";
    },
    removeGoogleLoginMessage: (state) => {
      state.googleLoginStatus = "idle";
      state.googleLoginError = "";
    },
    removeLoginWithTokenMessage: (state) => {
      state.loginWithTokenStatus = "idle";
      state.loginWithTokenError = "";
    },
    logoutComplete: (state) => {},
  },
  extraReducers: (builder) => {
    //Login
    builder
      .addCase(login.pending, (state, action) => {
        // console.log(action);
        state.status = "loading";
        state.error = "";
      })
      .addCase(login.fulfilled, (state, action) => {
        // console.log(action);
        state.status = "success";
        state.error = "";
        state.accessToken = action.payload.accessToken;
        state.data = action.payload.data;
      })
      .addCase(login.rejected, (state, action) => {
        // console.log(action.payload);
        state.status = "failed";
        state.error = action.payload.message;
      })
      // Sign Up
      .addCase(signup.pending, (state, action) => {
        state.registerStatus = "loading";
        state.registerError = "";
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.registerStatus = "success";
        state.registerError = "";
      })
      .addCase(signup.rejected, (state, action) => {
        state.registerStatus = "failed";
        state.registerError = action.payload.message;
      })
      // Forgot password
      .addCase(forgotPassword.pending, (state, action) => {
        state.forgotPasswordStatus = "loading";
        state.forgotPasswordError = "";
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotPasswordStatus = "success";
        state.forgotPasswordError = "";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordStatus = "failed";
        state.forgotPasswordError = action.payload.message;
      })
      // Reset password
      .addCase(resetPassword.pending, (state, action) => {
        state.resetPasswordStatus = "loading";
        state.resetPasswordError = "";
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordStatus = "success";
        state.resetPasswordError = "";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordStatus = "failed";
        state.resetPasswordError = action.payload.message;
      })
      // Verify user
      .addCase(verifyUser.pending, (state, action) => {
        state.verifyUserStatus = "loading";
        state.verifyUserError = "";
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.verifyUserStatus = "success";
        state.verifyUserError = "";
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.verifyUserStatus = "failed";
        state.verifyUserError = action.payload.message;
      })
      //Login with token
      .addCase(loginwithtoken.pending, (state, action) => {
        // console.log(action);
        state.loginWithTokenStatus = "loading";
        state.loginWithTokenError = "";
      })
      .addCase(loginwithtoken.fulfilled, (state, action) => {
        // console.log(action);
        state.loginWithTokenStatus = "success";
        state.loginWithTokenError = "";
        state.data = action.payload.data;
      })
      .addCase(loginwithtoken.rejected, (state, action) => {
        // console.log(action.payload);
        state.accessToken = null;
        localStorage.removeItem("accessToken");
        state.loginWithTokenStatus = "failed";
        state.loginWithTokenError = action.payload.message;
        // console.log(action);
      })
      // Google login
      .addCase(loginWithGoogle.pending, (state, action) => {
        // console.log(action);
        state.googleLoginStatus = "loading";
        state.googleLoginError = "";
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        // console.log(action);
        state.googleLoginStatus = "success";
        state.googleLoginError = "";
        state.accessToken = action.payload.accessToken;
        state.data = action.payload.data;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        // console.log(action.payload);
        state.googleLoginStatus = "failed";
        state.googleLoginError = action.payload.message;
        // console.log(action);
      })
      //Logout
      .addCase(logout.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.data = null;
        state.accessToken = null;
        state.status = "idle";
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload.message;
      });
  },
});

export const {
  removeLoginMessage,
  removeRegisterMessage,
  removeForgotPasswordMessage,
  removeGoogleLoginMessage,
  removeResetPasswordMessage,
  removeVerifyUserMessage,
  removeLoginWithTokenMessage,
  logoutComplete,
} = userSlice.actions;

export const getUser = (state) => state.user.data;
export const getUserName = (state) => state.user.data?.name;
export const getUserEmail = (state) => state.user.data?.email;
export const getUserID = (state) => state.user.data?.id;

export const getAccessToken = (state) => state.user.accessToken;

export const getStatus = (state) => state.user.status;
export const getError = (state) => state.user.error;

export const getRegisterStatus = (state) => state.user.registerStatus;
export const getRegisterError = (state) => state.user.registerError;

export const getLoginWithTokenStatus = (state) =>
  state.user.loginWithTokenStatus;
export const getLoginWithTokenError = (state) => state.user.loginWithTokeEerror;

export const getForgotPasswordStatus = (state) =>
  state.user.forgotPasswordStatus;
export const getForgotPasswordError = (state) => state.user.forgotPasswordError;

export const getResetPasswordStatus = (state) => state.user.resetPasswordStatus;
export const getResetPasswordError = (state) => state.user.resetPasswordError;

export const getVerifyUserStatus = (state) => state.user.verifyUserStatus;
export const getVerifyUserError = (state) => state.user.verifyUserError;

export const getGoogleLoginStatus = (state) => state.user.googleLoginStatus;
export const getGoogleLoginError = (state) => state.user.googleLoginError;

export default userSlice.reducer;
